import type {
  DayWidgetWeekEntry,
  WidgetConfig,
  WidgetWeekEntry,
} from "@/lib/schema";
import { formatWidgetRef, parseWidgetRef } from "@/lib/widget-refs";
import { getWidgetModule, isDayWidgetEntry } from "@/widgets/registry";
import { partsOf } from "@/widgets/types";

interface RefScore {
  score: number;
  /** Sum of the references' Week goals in this snapshot. */
  expected: number;
  /** True when at least one reference resolved to an instance here. */
  matched: boolean;
}

/**
 * Score references — whole instances or Scorable parts — against one week's
 * snapshot. Shared by Summary rows and a cycle's Points targets, so both read
 * a week identically.
 *
 * A disabled instance matches but earns nothing and expects nothing. A part
 * this build no longer declares is treated like a missing widget rather than
 * counting the whole.
 */
export function scoreRefs(
  refs: string[],
  entries: WidgetWeekEntry[],
): RefScore {
  let score = 0;
  let expected = 0;
  let matched = false;
  for (const ref of refs) {
    const { widgetId, partKey } = parseWidgetRef(ref);
    const entry = entries.find((e) => e.config.id === widgetId);
    if (!entry) continue;
    if (!entry.config.enabled) {
      matched = true;
      continue;
    }
    if (!isDayWidgetEntry(entry)) continue;
    const dayEntry = entry as DayWidgetWeekEntry;
    const module = getWidgetModule(dayEntry.config.kind);
    if (module?.scope !== "day") continue;
    if (module.scoring !== "points") continue;
    const runtime = { week: dayEntry.week, days: dayEntry.days };

    if (partKey !== undefined) {
      const part = module.parts?.find((p) => p.key === partKey);
      if (!part) continue;
      matched = true;
      score += part.scoreWeek(dayEntry.config, runtime);
      const goal = part.weekGoal(dayEntry.config);
      if (Number.isFinite(goal)) expected += goal;
      continue;
    }

    matched = true;
    score += module.scoreWeek(dayEntry.config, runtime);
    const weekGoal = (dayEntry.config as { weekGoal?: number }).weekGoal;
    if (typeof weekGoal === "number" && Number.isFinite(weekGoal)) {
      expected += weekGoal;
    }
  }
  return { score, expected, matched };
}

/**
 * The references an instance offers: none unless it is an enabled day widget
 * that can currently score; its parts when it has them, never the whole
 * beside them (docs/adr/0010); otherwise the whole. A pure-tracking meal-log
 * (maxScoreWeek 0) offers nothing.
 */
export function scorableRefs(widget: WidgetConfig): string[] {
  if (!widget.enabled) return [];
  const module = getWidgetModule(widget.kind);
  if (module?.scope !== "day") return [];
  if (module.scoring !== "points") return [];
  if (module.maxScoreWeek(widget) <= 0) return [];
  const parts = partsOf(module, widget);
  if (parts.length > 0) {
    return parts.map((part) => formatWidgetRef(widget.id, part.key));
  }
  return [widget.id];
}

/**
 * "Arbeidsdag · Arbeidstid" for a part, the instance label for a whole, or
 * null when no widget in `widgets` carries the id.
 */
export function refLabel(ref: string, widgets: WidgetConfig[]): string | null {
  const { widgetId, partKey } = parseWidgetRef(ref);
  const widget = widgets.find((w) => w.id === widgetId);
  if (!widget) return null;
  if (partKey === undefined) return widget.label;
  const module = getWidgetModule(widget.kind);
  const part =
    module?.scoring === "points"
      ? module.parts?.find((p) => p.key === partKey)
      : undefined;
  return part ? `${widget.label} · ${part.label}` : widget.label;
}
